/**
 * Shared header-nav helpers.
 *
 * Sixteen layouts and headers each carried their own copy of these two pieces —
 * the "turn a published page into a nav link" map and the label derivation. The
 * copies were identical apart from whitespace, which is exactly the shape that
 * lets a fix land in one storefront template and quietly miss the other nine.
 * (The header menus that ignored the builder's navItems were the same story.)
 *
 * `isLinkActive` is deliberately NOT here: those implementations genuinely
 * differ per template, because single-page templates match `#anchors` while
 * store templates match paths.
 */

export interface HeaderNavLink {
  key: string;
  label: string;
  href: string;
  accent?: boolean;
}

/**
 * Is this nav link the page we're on?
 *
 * There were 40 copies of this across the layouts, and they differed only in
 * which special cases each one happened to include — not in intent. Challenger
 * matched paths exactly, so "Shop" went dim the moment a shopper opened a
 * product. Bloom handled `/cart`, Atelier handled `/contact`, several handled
 * neither. That is drift, not design, so this is the union of what they were
 * each reaching for:
 *
 *   - `/products` also covers `/products/:slug`, so Shop stays lit on a PDP;
 *   - `/cart` likewise;
 *   - a dynamic page matches whether it is linked as `/about` or the legacy
 *     `/pages/about` (the latter is a redirect to the former);
 *   - `#anchors` are never "active" by path — single-page templates scroll to
 *     them and track their own state.
 */
export function isNavLinkActive(href: string, pathname: string): boolean {
  if (!href || href.startsWith('#')) return false;

  const link = href.toLowerCase().replace(/\/+$/, '') || '/';
  const path = (pathname || '/').toLowerCase().replace(/\/+$/, '') || '/';

  if (link === '/') return path === '/';
  if (link === '/products' || link === '/cart') {
    return path === link || path.startsWith(`${link}/`);
  }
  return path === link || path === `/pages${link}`;
}

/** Page title if it has one, else a Title-Cased version of its slug. */
export function pageLabel(page: any): string {
  const title = String(page?.title || '').trim();
  if (title) return title;
  const slug = String(page?.slug || '').trim();
  if (!slug) return 'Untitled';
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Pages that belong in a footer, not a header menu. Legal and utility pages
 * are reachable from the footer on every template; putting them in the nav
 * pushes a header past its layout — Atelier's centred wordmark ended up
 * overlapped by "PRIVACY POLICY" the moment those pages existed.
 */
const NOT_IN_HEADER_NAV = new Set(['privacy', 'terms', 'cart', 'checkout', 'privacy-policy', 'terms-conditions']);

/**
 * A tenant's published pages as header links, skipping any without a usable
 * slug. `uppercase` matches the templates that set their nav in caps.
 *
 * Capped by default: a header has a fixed amount of room, and a merchant who
 * publishes ten pages should not silently break their own chrome. The footer
 * still lists everything.
 */
export function dynamicPageLinks(
  pages: any[] | undefined,
  labelFor: (page: any) => string = pageLabel,
  opts: { uppercase?: boolean; limit?: number } = {},
): HeaderNavLink[] {
  const limit = typeof opts.limit === 'number' ? opts.limit : 2;
  return (pages || [])
    .filter((page) => !NOT_IN_HEADER_NAV.has(String(page?.slug || '').trim().toLowerCase().replace(/^\/+/, '')))
    .slice(0, limit)
    .map((page) => {
      const slug = String(page?.slug || '').trim().replace(/^\/+/, '');
      if (!slug) return null;
      const label = labelFor(page);
      return {
        key: `page-${page?.id ?? slug}`,
        label: opts.uppercase ? label.toUpperCase() : label,
        href: `/${slug}`,
      };
    })
    .filter(Boolean) as HeaderNavLink[];
}
